import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getJournalEntries, deleteJournalEntry } from '../../services/api';
import { fmtCurrency } from '../../utils/formatNumber';
import styles from './JournalEntries.module.css';

const ENTRY_TYPE_LABELS = {
  adjustment:        { label: 'Adjustment',        color: '#6b7280' },
  donation_transfer: { label: 'Donation Transfer',  color: '#10b981' },
  loan_received:     { label: 'Loan Received',      color: '#3b82f6' },
  loan_repayment:    { label: 'Loan Repayment',     color: '#8b5cf6' },
  loan_given:        { label: 'Loan Given',         color: '#f59e0b' },
  asset_purchase:    { label: 'Asset Purchase',     color: '#ef4444' },
  fund_transfer:     { label: 'Fund Transfer',      color: '#0ea5e9' },
  cash_deposit:      { label: 'Cash Deposit',       color: '#14b8a6' },
  cash_withdrawal:   { label: 'Cash Withdrawal',    color: '#f97316' },
};

const PAGE_SIZE = 50;

const JournalEntriesPage = () => {
  const {
    activeDerasarId,
    visibleFinancialYears,
    activeFinancialYearId,
    setActiveFinancialYearId,
    can,
  } = useAuth();
  const navigate = useNavigate();

  const [entries,    setEntries]    = useState([]);
  const [years,      setYears]      = useState([]);
  const [filterYear, setFilterYear] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [loading,    setLoading]    = useState(true);
  const [delTarget,  setDelTarget]  = useState(null);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const params = { derasarId: activeDerasarId };
      if (filterYear) params.financialYearId = filterYear;
      if (filterFromDate) params.fromDate = filterFromDate;
      if (filterToDate) params.toDate = filterToDate;
      const eRes = await getJournalEntries(params);
      setEntries(Array.isArray(eRes.data) ? eRes.data : []);
      setPage(0);
    } catch {
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, filterYear, filterFromDate, filterToDate]);

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setYears(visibleFinancialYears);
    setFilterYear(activeFinancialYearId || '');
  }, [activeDerasarId, visibleFinancialYears, activeFinancialYearId]);


  const selectedFilterYear = useMemo(
    () => years.find((year) => String(year.id) === String(filterYear)) || null,
    [years, filterYear],
  );

  const filterMinDate = selectedFilterYear?.startDate?.slice(0, 10) || '';
  const filterMaxDate = selectedFilterYear?.endDate?.slice(0, 10) || '';

  useEffect(() => {
    if (!filterYear) {
      setFilterFromDate('');
      setFilterToDate('');
      return;
    }

    setFilterFromDate(filterMinDate || '');
    setFilterToDate(filterMaxDate || '');
  }, [filterYear, filterMinDate, filterMaxDate]);

  const totalPages = Math.ceil(entries.length / PAGE_SIZE);
  const visibleEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleDelete = async () => {
    try {
      await deleteJournalEntry(delTarget.id, activeDerasarId);
      toast.success('Journal entry deleted');
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (!activeDerasarId)
    return <div className="empty-state"><p>Select a Derasar to view journal entries.</p></div>;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <div>
          <h2>Journal Entries</h2>
          <p>Manual double-entry adjustments</p>
        </div>
        {can('journalEntries', 'manage') && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/accounting/journal-entries/new')}
            disabled={!!selectedFilterYear?.isClosed}
            title={selectedFilterYear?.isClosed ? 'This financial year is locked — unlock it before adding new journal entries.' : undefined}
          >
            {selectedFilterYear?.isClosed ? <FiLock /> : <FiPlus />} New Entry
          </button>
        )}
      </div>

      <div className={styles.toolbar}>
        <div className="form-group">
          <label>Financial Year</label>
          <select
            value={filterYear}
            onChange={(e) => {
              setFilterYear(e.target.value);
              if (e.target.value) setActiveFinancialYearId(e.target.value);
            }}
          >
            <option value="">All Years</option>
            {years.map((y) => <option key={y.id} value={y.id}>{y.yearLabel}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>From Date</label>
          <input
            type="date"
            value={filterFromDate}
            min={filterMinDate || undefined}
            max={filterToDate || filterMaxDate || undefined}
            onChange={(e) => setFilterFromDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>To Date</label>
          <input
            type="date"
            value={filterToDate}
            min={filterFromDate || filterMinDate || undefined}
            max={filterMaxDate || undefined}
            onChange={(e) => setFilterToDate(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Voucher No</th>
                  <th>Type</th>
                  <th>Narration</th>
                  <th>Debit Account</th>
                  <th>Credit Account</th>
                  <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                  <th style={{ width: 60 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.length === 0 && (
                  <tr><td colSpan={8}><div className="empty-state">No journal entries yet.</div></td></tr>
                )}
                {visibleEntries.map((e) => {
                  const drLine = e.lines?.find((l) => l.type === 'debit');
                  const crLine = e.lines?.find((l) => l.type === 'credit');
                  return (
                    <tr key={e.id}>
                      <td>{e.entryDate}</td>
                      <td><code>{e.voucherNo}</code></td>
                      <td>
                        {(() => {
                          const t = ENTRY_TYPE_LABELS[e.entryType];
                          return t ? (
                            <span style={{
                              fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px',
                              borderRadius: 4, background: t.color + '1a', color: t.color,
                              whiteSpace: 'nowrap',
                            }}>{t.label}</span>
                          ) : <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{e.entryType}</span>;
                        })()}
                      </td>
                      <td>{e.narration}</td>
                      <td className={styles.drTag}>
                        {drLine?.account ? (
                          <div>
                            <div>{drLine.account.name}</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                              {drLine.account.accountCode && <code style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{drLine.account.accountCode}</code>}
                              {drLine.account.group?.nature && <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{drLine.account.group.nature}</span>}
                            </div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className={styles.crTag}>
                        {crLine?.account ? (
                          <div>
                            <div>{crLine.account.name}</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                              {crLine.account.accountCode && <code style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{crLine.account.accountCode}</code>}
                              {crLine.account.group?.nature && <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{crLine.account.group.nature}</span>}
                            </div>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtCurrency(drLine?.amount)}</td>
                      <td>
                        <div className={styles.actions}>
                          {can('journalEntries', 'manage') && (
                            <button className="btn btn-danger btn-sm" onClick={() => setDelTarget(e)} title="Delete">
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className={styles.paginationBar}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>← Prev</button>
            <span>Page {page + 1} of {totalPages} &nbsp;·&nbsp; {entries.length} total</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>Next →</button>
          </div>
        )}
      </div>

      {delTarget && (
        <div className="modal-overlay" onClick={() => setDelTarget(null)}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="card-header"><h3>Delete Journal Entry</h3></div>
            <div style={{ padding: '16px 20px' }}>
              <p>Delete entry <strong>{delTarget.voucherNo}</strong>?</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                This permanently removes the entry and its debit/credit lines. This cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDelTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntriesPage;
